import React, { useState, useEffect, useCallback } from 'react';
import { Check, Loader2, CalendarX, MapPin, Plus, X } from 'lucide-react';
import { supabaseUrl } from '../../lib/supabase/client';

/**
 * Öffentliche Mieterselbstauskunft (/selbstauskunft/:slug) — Interessenten füllen
 * ohne Login ein Standard-Formular aus; die Antworten landen strukturiert in
 * `selbstauskuenfte.daten` (Schema siehe docs/superpowers/specs/
 * 2026-07-24-mieterselbstauskunft-design.md) und lösen eine Admin-Benachrichtigung
 * aus. Datenquelle ist die Edge Function `selbstauskunft` (GET liefert Link-Infos,
 * POST übermittelt die Auskunft). Frosted-Look wie BuchungsSeite, aber max-w-2xl
 * wegen der Formularlänge.
 */

const API = `${supabaseUrl}/functions/v1/selbstauskunft`;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface LinkInfo {
  titel: string;
  beschreibung: string | null;
  objektAdresse: string | null;
}

interface PersonZeile {
  name: string;
  geburtsdatum: string;
  verhaeltnis: string;
}

interface FormState {
  anrede: string;
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  strasse: string;
  plz: string;
  ort: string;
  telefon: string;
  email: string;
  familienstand: string;
  personen: PersonZeile[];
  beruf: string;
  arbeitgeber: string;
  arbeitgeber_ort: string;
  beschaeftigt_seit: string;
  beschaeftigungsart: string;
  netto_einkommen: string;
  weitere_einkuenfte: string;
  mietverhaeltnis: {
    wohnhaft_seit: string;
    vermieter_name: string;
    vermieter_kontakt: string;
    gekuendigt: boolean;
    mietrueckstaende: boolean;
  };
  erklaerungen: {
    mietschulden: boolean;
    raeumungsklage: boolean;
    insolvenz: boolean;
    lohnpfaendung: boolean;
  };
  haustiere: string;
  raucher: boolean;
  musikinstrumente: string;
  einzugstermin: string;
  nachricht: string;
  einwilligung_datenschutz: boolean;
  wahrheitserklaerung: boolean;
}

const leeresFormular: FormState = {
  anrede: '',
  vorname: '',
  nachname: '',
  geburtsdatum: '',
  strasse: '',
  plz: '',
  ort: '',
  telefon: '',
  email: '',
  familienstand: '',
  personen: [],
  beruf: '',
  arbeitgeber: '',
  arbeitgeber_ort: '',
  beschaeftigt_seit: '',
  beschaeftigungsart: '',
  netto_einkommen: '',
  weitere_einkuenfte: '',
  mietverhaeltnis: {
    wohnhaft_seit: '',
    vermieter_name: '',
    vermieter_kontakt: '',
    gekuendigt: false,
    mietrueckstaende: false,
  },
  erklaerungen: {
    mietschulden: false,
    raeumungsklage: false,
    insolvenz: false,
    lohnpfaendung: false,
  },
  haustiere: '',
  raucher: false,
  musikinstrumente: '',
  einzugstermin: '',
  nachricht: '',
  einwilligung_datenschutz: false,
  wahrheitserklaerung: false,
};

const inputCls =
  'w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/15 text-sm font-semibold text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#c4a77d]/40 focus:border-[#c4a77d]/40 transition-all';
const dateCls = `${inputCls} [color-scheme:dark]`;
const optionCls = 'text-gray-900';

const Zwischenueberschrift: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#c4a77d] mb-3 mt-8 first:mt-0">
    {children}
  </p>
);

const JaNeinToggle: React.FC<{ label: string; value: boolean; onChange: (wert: boolean) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <div className="flex flex-col items-start gap-2 platz:flex-row platz:items-center platz:justify-between platz:gap-4 py-1.5">
    <span className="min-w-0 text-sm font-semibold text-white/80">{label}</span>
    <div className="flex shrink-0 rounded-xl bg-white/5 border border-white/15 p-1">
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`min-h-[44px] platz:min-h-0 px-4 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wide transition-all ${
          !value ? 'bg-[#c4a77d] text-white' : 'text-white/50 hover:text-white/80'
        }`}
      >
        Nein
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`min-h-[44px] platz:min-h-0 px-4 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wide transition-all ${
          value ? 'bg-[#c4a77d] text-white' : 'text-white/50 hover:text-white/80'
        }`}
      >
        Ja
      </button>
    </div>
  </div>
);

const SelbstauskunftSeite: React.FC = () => {
  const slug = window.location.pathname.split('/')[2] || '';

  const [laden, setLaden] = useState(true);
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [fehlerLaden, setFehlerLaden] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(leeresFormular);

  const [sende, setSende] = useState(false);
  const [fehlerSenden, setFehlerSenden] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState(false);

  const ladeInfo = useCallback(async () => {
    if (!slug) {
      setFehlerLaden('Dieser Link ist ungültig.');
      setLaden(false);
      return;
    }
    try {
      const res = await fetch(`${API}?slug=${encodeURIComponent(slug)}`);
      const d = await res.json();
      if (d.ok) {
        setInfo({ titel: d.titel, beschreibung: d.beschreibung, objektAdresse: d.objektAdresse });
        setFehlerLaden(null);
      } else {
        setInfo(null);
        setFehlerLaden(d.fehler || 'Dieser Link ist ungültig.');
      }
    } catch {
      setFehlerLaden('Verbindungsfehler — bitte laden Sie die Seite neu.');
    } finally {
      setLaden(false);
    }
  }, [slug]);

  useEffect(() => {
    ladeInfo();
  }, [ladeInfo]);

  const setFeld = <K extends keyof FormState>(feld: K, wert: FormState[K]) =>
    setForm((f) => ({ ...f, [feld]: wert }));

  const setMietverhaeltnis = <K extends keyof FormState['mietverhaeltnis']>(
    feld: K,
    wert: FormState['mietverhaeltnis'][K],
  ) => setForm((f) => ({ ...f, mietverhaeltnis: { ...f.mietverhaeltnis, [feld]: wert } }));

  const setErklaerung = <K extends keyof FormState['erklaerungen']>(
    feld: K,
    wert: FormState['erklaerungen'][K],
  ) => setForm((f) => ({ ...f, erklaerungen: { ...f.erklaerungen, [feld]: wert } }));

  const personHinzufuegen = () =>
    setForm((f) => ({ ...f, personen: [...f.personen, { name: '', geburtsdatum: '', verhaeltnis: '' }] }));

  const personEntfernen = (index: number) =>
    setForm((f) => ({ ...f, personen: f.personen.filter((_, i) => i !== index) }));

  const personAendern = (index: number, feld: keyof PersonZeile, wert: string) =>
    setForm((f) => ({
      ...f,
      personen: f.personen.map((p, i) => (i === index ? { ...p, [feld]: wert } : p)),
    }));

  const emailGueltig = EMAIL_REGEX.test(form.email.trim());
  const kannSenden =
    form.vorname.trim().length > 0 &&
    form.nachname.trim().length > 0 &&
    form.telefon.trim().length > 0 &&
    emailGueltig &&
    form.einwilligung_datenschutz &&
    form.wahrheitserklaerung;

  const absenden = async () => {
    if (!kannSenden) return;
    setSende(true);
    setFehlerSenden(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slug,
          daten: {
            ...form,
            vorname: form.vorname.trim(),
            nachname: form.nachname.trim(),
            telefon: form.telefon.trim(),
            email: form.email.trim(),
          },
        }),
      });
      const d = await res.json();
      if (d.ok) {
        setErfolg(true);
      } else {
        setFehlerSenden(d.fehler || 'Das hat leider nicht geklappt.');
      }
    } catch {
      setFehlerSenden('Verbindungsfehler — bitte erneut versuchen.');
    } finally {
      setSende(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1d24] p-6">
      <div className="w-full max-w-2xl bg-white/[0.06] border border-white/10 rounded-[2rem] backdrop-blur-xl p-10 text-center">
        <p className="text-[10px] font-black text-[#c4a77d] uppercase tracking-[0.3em] mb-8">
          Immobilienmanagement Schwede
        </p>

        {laden && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 size={28} className="text-[#c4a77d] animate-spin" />
            <p className="text-xs font-semibold text-white/40 uppercase tracking-widest">Formular wird geladen…</p>
          </div>
        )}

        {!laden && !info && (
          <div className="py-4">
            <CalendarX size={36} className="text-white/30 mx-auto mb-5" />
            <h1 className="text-lg font-black text-white mb-2">Link ungültig</h1>
            <p className="text-sm text-white/60 leading-relaxed">{fehlerLaden}</p>
          </div>
        )}

        {!laden && info && erfolg && (
          <div className="py-4">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center bg-emerald-500/20 text-emerald-400">
              <Check size={30} />
            </div>
            <h1 className="text-lg font-black text-white mb-3">Vielen Dank!</h1>
            <p className="text-sm text-white/70 leading-relaxed">
              Ihre Selbstauskunft ist bei uns eingegangen — wir melden uns zeitnah bei Ihnen.
            </p>
          </div>
        )}

        {!laden && info && !erfolg && (
          <>
            <h1 className="text-2xl font-black text-white mb-2">{info.titel}</h1>
            {info.beschreibung && (
              <p className="text-sm text-white/60 leading-relaxed mb-3">{info.beschreibung}</p>
            )}
            {info.objektAdresse && (
              <p className="flex items-center justify-center gap-1.5 text-xs text-white/50 mb-6">
                <MapPin size={13} /> {info.objektAdresse}
              </p>
            )}

            <div className="text-left">
              {/* ── 1. Persönliche Angaben ─────────────────────────────────── */}
              <Zwischenueberschrift>Persönliche Angaben</Zwischenueberschrift>
              <div className="space-y-3">
                <select
                  value={form.anrede}
                  onChange={(e) => setFeld('anrede', e.target.value)}
                  className={inputCls}
                >
                  <option value="" className={optionCls}>Anrede</option>
                  <option value="Herr" className={optionCls}>Herr</option>
                  <option value="Frau" className={optionCls}>Frau</option>
                  <option value="Divers" className={optionCls}>Divers</option>
                </select>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={form.vorname}
                    onChange={(e) => setFeld('vorname', e.target.value)}
                    placeholder="Vorname *"
                    autoComplete="given-name"
                    className={inputCls}
                  />
                  <input
                    type="text"
                    value={form.nachname}
                    onChange={(e) => setFeld('nachname', e.target.value)}
                    placeholder="Nachname *"
                    autoComplete="family-name"
                    className={inputCls}
                  />
                </div>
                <input
                  type="date"
                  value={form.geburtsdatum}
                  onChange={(e) => setFeld('geburtsdatum', e.target.value)}
                  placeholder="Geburtsdatum"
                  className={dateCls}
                />
                <input
                  type="text"
                  value={form.strasse}
                  onChange={(e) => setFeld('strasse', e.target.value)}
                  placeholder="Straße und Hausnummer"
                  autoComplete="street-address"
                  className={inputCls}
                />
                <div className="grid grid-cols-[110px_1fr] gap-3">
                  <input
                    type="text"
                    value={form.plz}
                    onChange={(e) => setFeld('plz', e.target.value)}
                    placeholder="PLZ"
                    autoComplete="postal-code"
                    className={inputCls}
                  />
                  <input
                    type="text"
                    value={form.ort}
                    onChange={(e) => setFeld('ort', e.target.value)}
                    placeholder="Ort"
                    autoComplete="address-level2"
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="tel"
                    value={form.telefon}
                    onChange={(e) => setFeld('telefon', e.target.value)}
                    placeholder="Telefon *"
                    autoComplete="tel"
                    className={inputCls}
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setFeld('email', e.target.value)}
                    placeholder="E-Mail-Adresse *"
                    autoComplete="email"
                    className={inputCls}
                  />
                </div>
                <input
                  type="text"
                  value={form.familienstand}
                  onChange={(e) => setFeld('familienstand', e.target.value)}
                  placeholder="Familienstand"
                  className={inputCls}
                />
              </div>

              {/* ── 2. Weitere einziehende Personen ────────────────────────── */}
              <Zwischenueberschrift>Weitere einziehende Personen</Zwischenueberschrift>
              <div className="space-y-3">
                {form.personen.map((person, index) => (
                  <div key={index} className="flex items-start gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/10">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={person.name}
                        onChange={(e) => personAendern(index, 'name', e.target.value)}
                        placeholder="Name"
                        className={inputCls}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="date"
                          value={person.geburtsdatum}
                          onChange={(e) => personAendern(index, 'geburtsdatum', e.target.value)}
                          className={dateCls}
                        />
                        <input
                          type="text"
                          value={person.verhaeltnis}
                          onChange={(e) => personAendern(index, 'verhaeltnis', e.target.value)}
                          placeholder="Verhältnis (z. B. Partner/in)"
                          className={inputCls}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => personEntfernen(index)}
                      aria-label="Person entfernen"
                      className="shrink-0 mt-1 w-8 h-8 flex items-center justify-center rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-all"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                {/* Deutlicher Block-Button statt blassem Text-Link — der Punkt wurde
                    im Formular leicht übersehen (User-Feedback 2026-07-28). */}
                <button
                  type="button"
                  onClick={personHinzufuegen}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-[#c4a77d]/60 text-sm font-bold text-[#c4a77d] hover:border-[#c4a77d] hover:bg-[#c4a77d]/10 transition-all active:scale-[0.99]"
                >
                  <Plus size={18} /> Person hinzufügen
                </button>
              </div>

              {/* ── 3. Beschäftigung & Einkommen ───────────────────────────── */}
              <Zwischenueberschrift>Beschäftigung &amp; Einkommen</Zwischenueberschrift>
              <div className="space-y-3">
                <input
                  type="text"
                  value={form.beruf}
                  onChange={(e) => setFeld('beruf', e.target.value)}
                  placeholder="Beruf"
                  className={inputCls}
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={form.arbeitgeber}
                    onChange={(e) => setFeld('arbeitgeber', e.target.value)}
                    placeholder="Arbeitgeber"
                    className={inputCls}
                  />
                  <input
                    type="text"
                    value={form.arbeitgeber_ort}
                    onChange={(e) => setFeld('arbeitgeber_ort', e.target.value)}
                    placeholder="Ort des Arbeitgebers"
                    className={inputCls}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="date"
                    value={form.beschaeftigt_seit}
                    onChange={(e) => setFeld('beschaeftigt_seit', e.target.value)}
                    className={dateCls}
                  />
                  <select
                    value={form.beschaeftigungsart}
                    onChange={(e) => setFeld('beschaeftigungsart', e.target.value)}
                    className={inputCls}
                  >
                    <option value="" className={optionCls}>Beschäftigungsart</option>
                    <option value="unbefristet" className={optionCls}>Unbefristet angestellt</option>
                    <option value="befristet" className={optionCls}>Befristet angestellt</option>
                    <option value="selbststaendig" className={optionCls}>Selbstständig</option>
                    <option value="rente" className={optionCls}>Rente/Pension</option>
                    <option value="studium_ausbildung" className={optionCls}>Studium/Ausbildung</option>
                    <option value="arbeitssuchend" className={optionCls}>Arbeitssuchend</option>
                  </select>
                </div>
                <input
                  type="text"
                  value={form.netto_einkommen}
                  onChange={(e) => setFeld('netto_einkommen', e.target.value)}
                  placeholder="Monatliches Haushalts-Nettoeinkommen (z. B. 2.800 €)"
                  className={inputCls}
                />
                <input
                  type="text"
                  value={form.weitere_einkuenfte}
                  onChange={(e) => setFeld('weitere_einkuenfte', e.target.value)}
                  placeholder="Weitere Einkünfte (optional)"
                  className={inputCls}
                />
              </div>

              {/* ── 4. Aktuelles Mietverhältnis ────────────────────────────── */}
              <Zwischenueberschrift>Aktuelles Mietverhältnis</Zwischenueberschrift>
              <div className="space-y-3">
                <input
                  type="date"
                  value={form.mietverhaeltnis.wohnhaft_seit}
                  onChange={(e) => setMietverhaeltnis('wohnhaft_seit', e.target.value)}
                  placeholder="Wohnhaft seit"
                  className={dateCls}
                />
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={form.mietverhaeltnis.vermieter_name}
                    onChange={(e) => setMietverhaeltnis('vermieter_name', e.target.value)}
                    placeholder="Name des Vermieters"
                    className={inputCls}
                  />
                  <input
                    type="text"
                    value={form.mietverhaeltnis.vermieter_kontakt}
                    onChange={(e) => setMietverhaeltnis('vermieter_kontakt', e.target.value)}
                    placeholder="Kontakt des Vermieters"
                    className={inputCls}
                  />
                </div>
                <JaNeinToggle
                  label="Mietverhältnis bereits gekündigt?"
                  value={form.mietverhaeltnis.gekuendigt}
                  onChange={(v) => setMietverhaeltnis('gekuendigt', v)}
                />
                <JaNeinToggle
                  label="Bestehen Mietrückstände?"
                  value={form.mietverhaeltnis.mietrueckstaende}
                  onChange={(v) => setMietverhaeltnis('mietrueckstaende', v)}
                />
              </div>

              {/* ── 5. Erklärungen ──────────────────────────────────────────── */}
              <Zwischenueberschrift>Erklärungen</Zwischenueberschrift>
              <div className="space-y-1">
                <JaNeinToggle
                  label="Bestehen Mietschulden bei einem vorherigen Vermieter?"
                  value={form.erklaerungen.mietschulden}
                  onChange={(v) => setErklaerung('mietschulden', v)}
                />
                <JaNeinToggle
                  label="Räumungsklage in den letzten 5 Jahren?"
                  value={form.erklaerungen.raeumungsklage}
                  onChange={(v) => setErklaerung('raeumungsklage', v)}
                />
                <JaNeinToggle
                  label="Eidesstattliche Versicherung/Insolvenzverfahren?"
                  value={form.erklaerungen.insolvenz}
                  onChange={(v) => setErklaerung('insolvenz', v)}
                />
                <JaNeinToggle
                  label="Lohn-/Gehaltspfändung?"
                  value={form.erklaerungen.lohnpfaendung}
                  onChange={(v) => setErklaerung('lohnpfaendung', v)}
                />
              </div>

              {/* ── 6. Sonstiges ─────────────────────────────────────────────── */}
              <Zwischenueberschrift>Sonstiges</Zwischenueberschrift>
              <div className="space-y-3">
                <input
                  type="text"
                  value={form.haustiere}
                  onChange={(e) => setFeld('haustiere', e.target.value)}
                  placeholder="Haustiere (leer = keine)"
                  className={inputCls}
                />
                <JaNeinToggle label="Raucher?" value={form.raucher} onChange={(v) => setFeld('raucher', v)} />
                <input
                  type="text"
                  value={form.musikinstrumente}
                  onChange={(e) => setFeld('musikinstrumente', e.target.value)}
                  placeholder="Musikinstrumente (optional)"
                  className={inputCls}
                />
                <input
                  type="date"
                  value={form.einzugstermin}
                  onChange={(e) => setFeld('einzugstermin', e.target.value)}
                  placeholder="Gewünschter Einzugstermin"
                  className={dateCls}
                />
                <textarea
                  value={form.nachricht}
                  onChange={(e) => setFeld('nachricht', e.target.value)}
                  placeholder="Nachricht (optional)"
                  rows={3}
                  className={`${inputCls} resize-none`}
                />
              </div>

              {/* ── 7. Einwilligungen ────────────────────────────────────────── */}
              <Zwischenueberschrift>Einwilligungen</Zwischenueberschrift>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.einwilligung_datenschutz}
                    onChange={(e) => setFeld('einwilligung_datenschutz', e.target.checked)}
                    className="mt-0.5 w-4 h-4 shrink-0 rounded border-white/30 bg-white/5 accent-[#c4a77d] cursor-pointer"
                  />
                  <span className="text-xs text-white/70 leading-relaxed">
                    Ich willige ein, dass meine Angaben zur Entscheidung über den Abschluss eines Mietvertrags
                    gespeichert und verarbeitet werden. Diese Einwilligung kann ich jederzeit widerrufen.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.wahrheitserklaerung}
                    onChange={(e) => setFeld('wahrheitserklaerung', e.target.checked)}
                    className="mt-0.5 w-4 h-4 shrink-0 rounded border-white/30 bg-white/5 accent-[#c4a77d] cursor-pointer"
                  />
                  <span className="text-xs text-white/70 leading-relaxed">
                    Ich versichere, dass alle Angaben wahrheitsgemäß und vollständig sind. Falsche Angaben können
                    zur Anfechtung oder Kündigung des Mietvertrags führen.
                  </span>
                </label>
              </div>
            </div>

            <button
              onClick={absenden}
              disabled={!kannSenden || sende}
              className="w-full mt-8 flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-[11px] font-black uppercase tracking-widest text-white bg-[#c4a77d] hover:bg-[#b3966a] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sende ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Selbstauskunft absenden
            </button>

            {fehlerSenden && <p className="mt-4 text-sm font-semibold text-red-400">{fehlerSenden}</p>}
          </>
        )}
      </div>
    </div>
  );
};

export default SelbstauskunftSeite;
